import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Rating as StarRating,
} from '@mui/material';
import MissionComment from 'types/entities/MissionComment';

type QuickActions = {
  cancellation: boolean;
  cancelsubmission: boolean;
  cancelvalidation: boolean;
  submission: boolean;
  validation: boolean;
  acceptation: boolean;
  delete: boolean;
  eval?: '5' | '4' | '3' | '2' | '1' | '0' | 'S' | null;
  comment?: MissionComment | null;
};

interface RatingPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  setQuickActions: React.Dispatch<React.SetStateAction<QuickActions>>;
}

const RatingPopup: React.FC<RatingPopupProps> = ({ open, onClose, onSubmit, setQuickActions }) => {
  const [rating, setRating] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [value, setValue] = React.useState<number>(0);
  // const [hover, setHover] = React.useState(-1);
  const starLabels: { [index: string]: string } = {
    5: '5',
    4: '4',
    3: '3',
    2: '2',
    1: '1',
    0: '0',
  };

  useEffect(() => {
    if (!open) {
      setRating('');
      setComment('');
    }
  }, [open]);

  const handleRatingChange = (value: string) => {
    setRating(value);
    if (value !== 'D') {
      setComment('');
    }
  };

  const handleSubmit = () => {
    if (rating === 'D' && !comment) {
      return;
    }

    const constructComment = () => {
      if (comment !== '' && comment !== null) {
        return {
          comment,
          is_private: false,
          rating: rating || undefined,
        };
      }
      return null;
    };

    setQuickActions((prevActions) => ({
      ...prevActions,
      eval: rating as '5' | '4' | '3' | '2' | '1' | '0' | 'S' | null,
      comment: constructComment() as MissionComment | null,
    }));

    onSubmit();
    onClose();
  };

  const handleSkip = () => {
    setQuickActions((prevActions) => ({
      ...prevActions,
      eval: 'S',
      comment: null,
    }));

    onSubmit();
    onClose();
  };

  function getStarLabelText(value: number) {
    return `${value} Star${value !== 1 ? 's' : ''}, ${starLabels[value]}`;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>Deliverables Assessment</DialogTitle>
      <DialogContent dividers>
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ marginBottom: '1rem', fontSize: 14 }}>Share your valuable ratings & feedback below</p>
          <Box sx={{ ml: -0.5 }}>
            <StarRating
              name="hover-feedback"
              value={value}
              precision={1}
              sx={{
                marginTop: -1,
                fontSize: '3.5rem',
                '& .MuiRating-icon': {
                  marginRight: '4px',
                },
              }}
              getLabelText={getStarLabelText}
              onChange={(event, newValue) => {
                const rating = newValue ?? 0;
                setValue(rating);
              }}
              // onChangeActive={(event, newHover) => {
              //   const hover = newHover ?? 0;
              //   setHover(hover);
              // }}
            />
          </Box>
        </div>
        <TextField
          autoFocus
          margin="dense"
          label={rating === 'D' ? 'Enter your comment to enable submit' : 'Enter your comment'}
          type="text"
          fullWidth
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <p style={{ marginTop: '1.5rem', fontSize: 14 }}>Note: Clicking &quot;skip&quot; will accept the deliverable</p>
        <p style={{ fontSize: 14 }}>without a rating. Status will be &quot;Not Assessed&quot;</p>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSkip}>skip</Button>
        <Button onClick={handleSubmit} disabled={!rating || (rating === 'D' && comment === '')}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatingPopup;
